import React, { useState, useEffect } from 'react';
import { Task, tasksApi } from '../../lib/tasks-api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../ui/LdSheet';
import { Button } from '../ld/Button';
import { MIcon } from '../../lib/micon';
import { useSchueler } from '../../contexts/SchuelerContext';
import { taskQuelleZiel } from '../../lib/insight-ziel';
import { LdDatum } from '../ld/LdFenster';

interface Props {
  task: Task | null;
  onClose: () => void;
  onAktualisiert: () => void;
  onGeloescht: () => void;
}

export function TaskEditModal({ task, onClose, onAktualisiert, onGeloescht }: Props) {
  const [titel,     setTitel]     = useState('');
  const [beschr,    setBeschr]    = useState('');
  const [faelligAm, setFaelligAm] = useState('');
  const [laden,     setLaden]     = useState(false);
  const [loeschenBestaetigung, setLoeschenBestaetigung] = useState(false);
  const { setOpenProfilId } = useSchueler();

  useEffect(() => {
    if (task) {
      setTitel(task.titel);
      setBeschr(task.beschreibung ?? '');
      setFaelligAm(task.faellig_am ?? '');
      setLoeschenBestaetigung(false);
    }
  }, [task]);

  if (!task) return null;

  const inp: React.CSSProperties = {
    width: '100%', padding: '10px 13px', borderRadius: 10,
    border: '1.5px solid var(--ld-outline-soft)', fontSize: 15, outline: 'none',
    boxSizing: 'border-box', background: 'var(--ld-bg-input)', color: 'var(--ld-text-primary)',
  };

  async function speichern() {
    if (!titel.trim()) return;
    setLaden(true);
    try {
      await tasksApi.aktualisieren(task!.id, {
        titel: titel.trim(),
        beschreibung: beschr.trim() || undefined,
        faellig_am: faelligAm || undefined,
      } as any);
      onAktualisiert();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  async function loeschen() {
    setLaden(true);
    try {
      await tasksApi.loeschen(task!.id);
      onGeloescht();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // Welle U6b: Aufgaben aus Radar/Mail führen zurück zu ihrer Quelle
  const zurQuelle = taskQuelleZiel(task, setOpenProfilId);

  const quelleLabel = task.quelle === 'diktat' ? 'aus Erzählen' : task.quelle === 'radar' ? 'aus einem Hinweis auf Heute' : task.quelle === 'mail' ? 'aus Mail' : task.quelle === 'gespraech' ? 'aus Gespräch' : null;

  return (
    <LdSheet open onClose={onClose} maxWidth={520} kopf={{ titel: 'Aufgabe bearbeiten', kontext: quelleLabel || null }}>
      <div>
        {zurQuelle && (
          <button onClick={() => { onClose(); zurQuelle(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14, padding: '7px 12px', borderRadius: 'var(--ld-radius-pill)', border: '1.5px solid var(--ld-field-border)', background: 'var(--ld-bg-card)', color: 'var(--ld-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <MIcon name={task.quelle === 'mail' ? 'mail' : task.quelle === 'gespraech' ? 'forum' : 'radar'} size={16} />Zur Quelle →
          </button>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ld-text-primary)', display: 'block', marginBottom: 5 }}>Aufgabe</label>
          <input value={titel} onChange={e => setTitel(e.target.value)} style={inp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ld-text-primary)', display: 'block', marginBottom: 5 }}>Beschreibung (optional)</label>
          <textarea value={beschr} onChange={e => setBeschr(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ld-text-primary)', display: 'block', marginBottom: 5 }}>Fällig am (optional)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <LdDatum value={faelligAm} onChange={e => setFaelligAm(e.target.value)} />
            {faelligAm && <button onClick={() => setFaelligAm('')} style={{ padding: '0 12px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 13, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>×</button>}
          </div>
        </div>

        {/* Löschen */}
        {!loeschenBestaetigung ? (
          <button onClick={() => setLoeschenBestaetigung(true)} disabled={laden} style={{ width: '100%', padding: '10px 0', borderRadius: 12, border: '1.5px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}>
            <MIcon name="delete" size={17} style={{ verticalAlign: '-3px', marginRight: 6 }} />Aufgabe löschen
          </button>
        ) : (
          <div style={{ background: '#FEF2F2', borderRadius: 12, padding: '12px 14px', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1, fontSize: 13, color: '#DC2626', fontWeight: 600 }}>Wirklich löschen?</span>
            <button onClick={() => setLoeschenBestaetigung(false)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'white', fontSize: 13, cursor: 'pointer' }}>Nein</button>
            <button onClick={loeschen} disabled={laden} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#DC2626', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Löschen</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Abbrechen</Button>
          <Button variant="primary" onClick={speichern} loading={laden} disabled={!titel.trim() || laden} style={{ flex: 2 }}>Speichern</Button>
        </div>
      </div>
    </LdSheet>
  );
}
